import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { paths } from "../routes";
import { CanvasUnsupportedError } from "./canvasSupport";
import "./games.css";

interface State {
  failed: boolean;
  /** The browser can't draw the game (rather than a download failing). */
  unsupported: boolean;
}

/**
 * Shown instead of a game that can't start — its code failed to download (offline, or a page
 * left open across a deploy) or the browser can't draw it. Never a blank screen.
 */
class GameErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { failed: false, unsupported: false };

  static getDerivedStateFromError(error: unknown): State {
    return { failed: true, unsupported: error instanceof CanvasUnsupportedError };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <ErrorCard unsupported={this.state.unsupported} />;
  }
}

function ErrorCard({ unsupported }: { unsupported: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="game-backdrop">
      <section className="game-card game-card--center" aria-labelledby="game-error-title">
        <h2 id="game-error-title" className="game-card__title">
          {t("error.title")}
        </h2>
        <p className="game-card__text">{t(unsupported ? "error.unsupported" : "error.offline")}</p>
        <Link className="game-button game-button--primary" to={paths.home}>
          {t("frame.backHome")}
        </Link>
      </section>
    </div>
  );
}

export default GameErrorBoundary;
